import { useTranslate } from '@refinedev/core';
import { Space, Table, Tag, Tooltip, Typography, theme } from 'antd';
import { orDash } from '@/components/common/show/shared';
import { TargetAvailabilityTag } from '@/components/user_catalog/banner_actions/BannerActionTags';
import { CreativeDimensions } from '@/components/user_catalog/creative/CreativeDimensions';
import { cropInfo, ratioLabel } from './bannerPreview';
import type { LinkRow, PreviewModel } from './bannerPreview';

const { Text } = Typography;

interface BannerLinksTableProps {
  rows: LinkRow[];
  /** Container ratio every page is forced to; drives the crop verdict. */
  effectiveSize: PreviewModel['effectiveSize'];
  /** Feed page shown in the preview panel; its row is marked. */
  selectedPage?: number;
}

export const BannerLinksTable = ({
  rows,
  effectiveSize,
  selectedPage,
}: BannerLinksTableProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  const isPreviewed = (row: LinkRow) =>
    row.page !== null && row.page === selectedPage;

  return (
    <Table<LinkRow>
      dataSource={rows}
      rowKey={(row) => row.link.id}
      pagination={false}
      size="small"
      onRow={(row) =>
        isPreviewed(row) ? { style: { background: token.colorPrimaryBg } } : {}
      }
    >
      <Table.Column<LinkRow>
        title={t('user_catalog.banners.fields.position')}
        width={120}
        render={(_, row) => (
          <Space size={4}>
            {row.position}
            {isPreviewed(row) && (
              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                {t('user_catalog.banners.links_table.previewed')}
              </Tag>
            )}
          </Space>
        )}
      />
      <Table.Column<LinkRow>
        title={t('user_catalog.banners.fields.banner_action')}
        render={(_, row) =>
          row.action ? (
            <Typography.Link
              href={`/user_catalog/banner_actions/${row.action.id}`}
            >
              {row.action.name}
            </Typography.Link>
          ) : (
            row.link.banner_action_id
          )
        }
      />
      <Table.Column<LinkRow>
        title={t('user_catalog.banners.links_table.size')}
        render={(_, row) =>
          orDash(
            row.action && (
              <Text
                style={
                  row.sizeClash ? { color: token.colorWarning } : undefined
                }
              >
                {t(`user_catalog.banner_actions.size.${row.action.size}`)}{' '}
                <Text type="secondary">{ratioLabel(row.action.size)}</Text>
              </Text>
            )
          )
        }
      />
      <Table.Column<LinkRow>
        title={t('user_catalog.banners.links_table.dimensions')}
        render={(_, row) => {
          const { image_width: w, image_height: h } = row.action ?? {};
          if (w == null || h == null) {
            return (
              <Text type="secondary">
                {t('user_catalog.banners.links_table.no_image')}
              </Text>
            );
          }
          // Only pages that reach the feed get cropped to the container.
          const crop =
            row.page !== null && row.action && effectiveSize
              ? cropInfo(row.action, effectiveSize)
              : null;
          return <CreativeDimensions width={w} height={h} crop={crop} />;
        }}
      />
      <Table.Column<LinkRow>
        title={t('user_catalog.banners.links_table.deeplink')}
        render={(_, row) =>
          orDash(
            row.deeplink && (
              <Tooltip title={row.deeplink}>
                <Text
                  code
                  copyable={{ text: row.deeplink }}
                  ellipsis
                  style={{ maxWidth: 200 }}
                >
                  {row.deeplink}
                </Text>
              </Tooltip>
            )
          )
        }
      />
      <Table.Column<LinkRow>
        title={t('user_catalog.banners.links_table.target')}
        render={(_, row) => {
          if (!row.deeplink) {
            // The image still renders in the app; the banner just does
            // nothing on tap — warn rather than hide.
            return (
              <Tag color="warning">
                {t('user_catalog.banners.links_table.target_none')}
              </Tag>
            );
          }
          // The action may not have loaded yet; don't guess at its kind.
          if (!row.action) return orDash(null);
          return (
            <Space size={4}>
              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                {t('user_catalog.banners.links_table.target_in_app', {
                  kind: t(
                    `user_catalog.banner_actions.kind.${row.action.kind}`
                  ),
                })}
              </Tag>
              {/* The link keeps rendering after its target dies — flag it;
                  true is implied by the in-app tag, so only false shows. */}
              {row.link.target_available === false && (
                <TargetAvailabilityTag value={false} />
              )}
            </Space>
          );
        }}
      />
    </Table>
  );
};
